import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Truck, CheckCircle, Download, Share2, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import { TrustSignals, ReturnPolicy } from '@/components/TrustBadges';
import PaymentBadges from '@/components/PaymentBadges';
import GraphicalReceipt from '@/components/GraphicalReceipt';
import { downloadReceipt, shareReceipt } from '@/lib/receiptImage';
import api, { formatPrice, getGuestId } from '@/lib/api';
import { toast } from 'sonner';

const NIGERIAN_STATES = ["FCT Abuja","Lagos","Ogun","Oyo","Rivers","Abia","Adamawa","Akwa Ibom","Anambra","Bauchi","Bayelsa","Benue","Borno","Cross River","Delta","Ebonyi","Edo","Ekiti","Enugu","Gombe","Imo","Jigawa","Kaduna","Kano","Katsina","Kebbi","Kogi","Kwara","Nasarawa","Niger","Ondo","Osun","Plateau","Sokoto","Taraba","Yobe","Zamfara"];

export default function CheckoutPage() {
    const { cart, clearCart, fetchCart } = useCart();
    const { user } = useAuth();
    const navigate = useNavigate();
    const receiptRef = useRef(null);
    const [shippingRates, setShippingRates] = useState({});
    const [settings, setSettings] = useState({});
    const [form, setForm] = useState({ name: '', email: '', phone: '', address: '', city: '', state: '', zip: '' });
    const [shippingFee, setShippingFee] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [orderComplete, setOrderComplete] = useState(false);
    const [orderData, setOrderData] = useState(null);
    const [receiptBusy, setReceiptBusy] = useState(false);
    // Coupon — applied client-side for UX preview, re-validated server-side at order create
    const [couponInput, setCouponInput] = useState('');
    const [coupon, setCoupon] = useState(null); // {code, discount_amount, message}
    const [couponLoading, setCouponLoading] = useState(false);

    const applyCoupon = async () => {
        const code = (couponInput || '').trim().toUpperCase();
        if (!code) { toast.error('Enter a code'); return; }
        setCouponLoading(true);
        try {
            const items = cart.items.map(i => ({ product_id: i.product_id, quantity: i.quantity }));
            const { data } = await api.post('/coupons/validate', { code, items });
            setCoupon(data);
            toast.success(data.message || 'Coupon applied');
        } catch (e) {
            setCoupon(null);
            toast.error(e?.response?.data?.detail || 'Coupon could not be applied');
        } finally { setCouponLoading(false); }
    };
    const removeCoupon = () => { setCoupon(null); setCouponInput(''); };

    const handleDownload = async () => {
        if (!receiptRef.current) return;
        setReceiptBusy(true);
        try {
            await downloadReceipt(receiptRef.current, orderData?.order_number);
            toast.success('Receipt downloaded');
        } catch (e) { toast.error('Could not generate receipt'); }
        finally { setReceiptBusy(false); }
    };
    const handleShare = async () => {
        if (!receiptRef.current) return;
        setReceiptBusy(true);
        try {
            const r = await shareReceipt(receiptRef.current, orderData?.order_number,
                `My BeautyByChancy order #${orderData?.order_number} — ${formatPrice(orderData?.total || 0)}`);
            if (r.shared) toast.success('Shared!');
            else if (r.downloaded) toast.success('Receipt downloaded (sharing not supported on this device)');
        } catch (e) { toast.error('Could not share receipt'); }
        finally { setReceiptBusy(false); }
    };

    useEffect(() => {
        api.get('/shipping/rates').then(({ data }) => setShippingRates(data)).catch(() => {});
        api.get('/settings').then(({ data }) => {
            setSettings(data);
            if (data.paystack_public_key) loadPaystackScript();
        }).catch(() => {});
        if (user) setForm(f => ({ ...f, name: user.name || '', email: user.email || '', phone: user.phone || '' }));
    }, [user]);

    // Robust Paystack inline.js loader — retries once on failure, idempotent.
    const loadPaystackScript = () => new Promise((resolve, reject) => {
        if (window.PaystackPop) { resolve(); return; }
        const existing = document.querySelector('script[src*="paystack"]');
        if (existing) {
            existing.addEventListener('load', () => resolve());
            existing.addEventListener('error', () => reject(new Error('script-error')));
            // If script already loaded earlier, PaystackPop should be there on next tick
            setTimeout(() => window.PaystackPop ? resolve() : reject(new Error('script-missing')), 2500);
            return;
        }
        const s = document.createElement('script');
        s.src = 'https://js.paystack.co/v1/inline.js';
        s.async = true;
        s.onload = () => resolve();
        s.onerror = () => reject(new Error('script-error'));
        document.head.appendChild(s);
    });

    useEffect(() => {
        if (form.state && shippingRates[form.state] !== undefined) {
            setShippingFee(shippingRates[form.state]);
        }
    }, [form.state, shippingRates]);

    const couponDiscount = Math.min(coupon?.discount_amount || 0, cart.total);
    const total = Math.max(0, cart.total - couponDiscount) + shippingFee;

    const handleSubmit = async () => {
        if (!form.name || !form.email || !form.phone || !form.address || !form.state) {
            toast.error('Please fill in all required fields');
            return;
        }
        if (cart.items.length === 0) { toast.error('Cart is empty'); return; }
        // Safe currency conversion: Paystack expects integer kobo.
        const amountKobo = Math.round(Number(total) * 100);
        if (!Number.isFinite(amountKobo) || amountKobo <= 0) {
            toast.error('Invalid order amount. Please refresh and try again.');
            return;
        }
        setSubmitting(true);
        const paystackKey = settings.paystack_public_key;
        if (!paystackKey) {
            // No Paystack configured → fall back to "Pay on Delivery" demo flow
            await createOrder(`demo-${Date.now()}`);
            return;
        }
        try {
            // Ensure inline.js is loaded (retry once if needed)
            try { await loadPaystackScript(); }
            catch (_) {
                try { await loadPaystackScript(); }
                catch (_) {
                    toast.error('Could not load Paystack. Please check your connection and try again.');
                    setSubmitting(false);
                    return;
                }
            }
            if (!window.PaystackPop || typeof window.PaystackPop.setup !== 'function') {
                toast.error('Payment gateway not ready. Please refresh the page and try again.');
                setSubmitting(false);
                return;
            }
            // Stable reference we control — matches order + Paystack dashboard
            const ref = `BBC-${Date.now()}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
            const handler = window.PaystackPop.setup({
                key: paystackKey,
                email: form.email,
                amount: amountKobo,
                currency: 'NGN',
                ref,
                channels: ['card', 'bank', 'ussd', 'bank_transfer', 'qr', 'mobile_money'],
                metadata: {
                    customer_name: form.name,
                    phone: form.phone,
                    state: form.state,
                    custom_fields: [
                        { display_name: 'Customer', variable_name: 'customer_name', value: form.name },
                        { display_name: 'Phone', variable_name: 'phone', value: form.phone },
                    ],
                },
                callback: (response) => { createOrder(response.reference || ref); },
                onClose: () => { setSubmitting(false); toast.info('Payment cancelled'); },
            });
            handler.openIframe();
        } catch (e) {
            toast.error('Checkout failed. Please try again.');
            setSubmitting(false);
        }
    };

    const createOrder = async (reference) => {
        try {
            const { data } = await api.post('/orders', {
                items: cart.items.map(i => ({ product_id: i.product_id, quantity: i.quantity, variant: i.variant })),
                shipping_address: form,
                payment_reference: reference,
                guest_id: getGuestId(),
                coupon_code: coupon?.code || '',
            });
            // Always verify payment (backend handles both real Paystack & demo mode)
            let verifiedStatus = null;
            let verifyErr = null;
            try {
                const { data: v } = await api.post('/payment/verify', { reference });
                if (v && v.status === 'success') verifiedStatus = 'paid';
                else verifyErr = v?.message || v?.gateway_response || 'Payment was not successful';
            } catch (err) {
                verifyErr = err?.response?.data?.detail || err?.response?.data?.message || 'Could not verify payment';
            }
            await fetchCart();
            if (verifiedStatus === 'paid') toast.success('Payment confirmed!');
            else if (verifyErr) toast.error(`Payment issue: ${verifyErr}. Order ${data.order_number}.`);
            // Post-payment redirect: always send customer to the live Order Status page.
            // The tracking page polls /api/orders/track/{ref} every 3s so webhook updates
            // (e.g. payment confirmed slightly after modal close) show up live.
            navigate(`/order/track/${encodeURIComponent(reference)}`, { replace: true });
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.response?.data?.message || 'Failed to create order';
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    if (orderComplete && orderData) {
        const waNum = settings.whatsapp_number || '07050657701';
        const cleanWa = waNum.replace(/\D/g, '');
        const intlWa = cleanWa.startsWith('0') ? `234${cleanWa.slice(1)}` : cleanWa;
        const waMsg = `Hi BeautyByChancy! I just placed order ${orderData.order_number} for ${formatPrice(orderData.total)}. Please confirm!`;
        return (
            <div className="min-h-[60vh] px-4 py-10" data-testid="order-success">
                <div className="max-w-2xl mx-auto">
                    <div className="flex flex-col items-center text-center mb-8">
                        <CheckCircle className="w-16 h-16 text-green-500 mb-3" />
                        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 mb-1">Order Confirmed!</h1>
                        <p className="font-body text-gray-500">Order <strong className="text-gray-900">{orderData.order_number}</strong> · <strong className="text-pink-600">{formatPrice(orderData.total)}</strong></p>
                    </div>

                    {/* Receipt preview — off-canvas render target is the same DOM node */}
                    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm p-3 sm:p-4 mb-6 overflow-x-auto">
                        <div style={{ width: 720, margin: '0 auto' }}>
                            <GraphicalReceipt ref={receiptRef} order={orderData} />
                        </div>
                    </div>

                    {/* Action row */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                        <Button onClick={handleDownload} disabled={receiptBusy} variant="outline"
                            className="rounded-full h-12 font-body gap-2 border-pink-200 text-pink-700 hover:bg-pink-50" data-testid="download-receipt-btn">
                            <Download className="w-4 h-4" /> {receiptBusy ? 'Working...' : 'Download HD Receipt (PNG)'}
                        </Button>
                        <Button onClick={handleShare} disabled={receiptBusy}
                            className="rounded-full h-12 font-body gap-2 bg-pink-600 hover:bg-pink-700 text-white" data-testid="share-receipt-btn">
                            <Share2 className="w-4 h-4" /> {receiptBusy ? 'Working...' : 'Share Receipt Image'}
                        </Button>
                    </div>

                    {/* WhatsApp confirmation */}
                    <a href={`https://wa.me/${intlWa}?text=${encodeURIComponent(waMsg)}`} target="_blank" rel="noreferrer"
                        className="w-full flex items-center justify-center gap-2 bg-[#25D366] text-white px-6 h-12 rounded-full font-body font-medium hover:bg-[#20b858] transition-colors" data-testid="wa-confirm-btn">
                        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.03L.789 23.647l4.768-1.249A11.932 11.932 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75c-2.17 0-4.207-.58-5.963-1.588l-.428-.254-4.437 1.163 1.183-4.32-.278-.443A9.724 9.724 0 012.25 12c0-5.385 4.365-9.75 9.75-9.75S21.75 6.615 21.75 12s-4.365 9.75-9.75 9.75z"/></svg>
                        Confirm on WhatsApp
                    </a>

                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Button onClick={() => navigate(user ? '/account' : '/login?redirect=/account')}
                            variant="outline" className="rounded-full h-12 font-body gap-2 border-gray-200" data-testid="view-my-orders-btn">
                            <Package className="w-4 h-4" /> View My Orders
                        </Button>
                        <Button onClick={() => navigate('/shop')} className="rounded-full h-12 font-body bg-gray-900 hover:bg-black text-white" data-testid="continue-shopping-btn">
                            Continue Shopping
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    if (cart.items.length === 0) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center px-4">
                <p className="font-body text-gray-500 mb-4">Your cart is empty</p>
                <Button onClick={() => navigate('/shop')} className="bg-pink-600 hover:bg-pink-700 rounded-full font-body">Go to Shop</Button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50" data-testid="checkout-page">
            <div className="max-w-5xl mx-auto px-4 py-8">
                <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 mb-8">Checkout</h1>
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                    {/* Form */}
                    <div className="lg:col-span-3 space-y-6">
                        <div className="bg-white rounded-xl p-6 shadow-sm">
                            <h2 className="font-heading text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2"><Truck className="w-5 h-5 text-pink-600" /> Shipping Information</h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div><Label className="font-body text-sm">Full Name *</Label><Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} className="mt-1 rounded-lg font-body" data-testid="checkout-name" /></div>
                                <div><Label className="font-body text-sm">Email *</Label><Input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} className="mt-1 rounded-lg font-body" data-testid="checkout-email" /></div>
                                <div><Label className="font-body text-sm">Phone *</Label><Input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} className="mt-1 rounded-lg font-body" data-testid="checkout-phone" /></div>
                                <div>
                                    <Label className="font-body text-sm">State *</Label>
                                    <Select value={form.state} onValueChange={v => setForm(f => ({...f, state: v}))}>
                                        <SelectTrigger className="mt-1 rounded-lg font-body" data-testid="checkout-state"><SelectValue placeholder="Select state" /></SelectTrigger>
                                        <SelectContent>{NIGERIAN_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div className="sm:col-span-2"><Label className="font-body text-sm">Address *</Label><Input value={form.address} onChange={e => setForm(f => ({...f, address: e.target.value}))} className="mt-1 rounded-lg font-body" data-testid="checkout-address" /></div>
                                <div><Label className="font-body text-sm">City</Label><Input value={form.city} onChange={e => setForm(f => ({...f, city: e.target.value}))} className="mt-1 rounded-lg font-body" data-testid="checkout-city" /></div>
                                <div><Label className="font-body text-sm">Zip Code</Label><Input value={form.zip} onChange={e => setForm(f => ({...f, zip: e.target.value}))} className="mt-1 rounded-lg font-body" data-testid="checkout-zip" /></div>
                            </div>
                        </div>
                    </div>
                    {/* Summary */}
                    <div className="lg:col-span-2">
                        <div className="bg-white rounded-xl p-6 shadow-sm sticky top-24">
                            <h2 className="font-heading text-lg font-semibold text-gray-900 mb-4">Order Summary</h2>
                            <div className="space-y-3 mb-4 max-h-48 overflow-y-auto">
                                {cart.items.map((item, idx) => (
                                    <div key={idx} className="flex items-center gap-3 text-sm font-body">
                                        <img src={item.image} alt={item.name} className="w-10 h-10 rounded-lg object-cover"  loading="lazy" decoding="async" />
                                        <div className="flex-1 min-w-0"><p className="truncate text-gray-900">{item.name}</p><p className="text-gray-500">x{item.quantity}</p></div>
                                        <span className="font-semibold text-gray-900">{formatPrice(item.price * item.quantity)}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="border-t pt-4 space-y-2 font-body text-sm">
                                <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>{formatPrice(cart.total)}</span></div>
                                {coupon && couponDiscount > 0 && (
                                    <div className="flex justify-between text-pink-600 font-medium" data-testid="checkout-coupon-line">
                                        <span className="flex items-center gap-1.5">
                                            <span className="text-[10px] uppercase tracking-wider bg-pink-100 text-pink-700 px-1.5 py-0.5 rounded-full font-bold">{coupon.code}</span>
                                            Coupon
                                        </span>
                                        <span>-{formatPrice(couponDiscount)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between"><span className="text-gray-500">Shipping{form.state ? ` (${form.state})` : ''}</span><span>{form.state ? formatPrice(shippingFee) : 'Select state'}</span></div>
                                <div className="flex justify-between text-lg font-bold pt-2 border-t"><span>Total</span><span className="text-pink-600" data-testid="checkout-total">{formatPrice(total)}</span></div>
                            </div>

                            {/* Promo code — apply BEFORE payment */}
                            <div className="mt-4 pt-4 border-t" data-testid="checkout-coupon-block">
                                {!coupon ? (
                                    <>
                                        <p className="text-[11px] uppercase tracking-wider font-body text-gray-500 mb-1.5">Promo code</p>
                                        <div className="flex gap-2">
                                            <Input
                                                value={couponInput}
                                                onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                                                onKeyDown={(e) => { if (e.key === 'Enter') applyCoupon(); }}
                                                placeholder="Enter code"
                                                className="font-body uppercase tracking-wider text-sm"
                                                data-testid="checkout-coupon-input"
                                                disabled={couponLoading}
                                            />
                                            <Button
                                                onClick={applyCoupon}
                                                disabled={couponLoading || !couponInput.trim()}
                                                variant="outline"
                                                className="rounded-full font-body border-pink-200 text-pink-700 hover:bg-pink-50 hover:text-pink-800 hover:border-pink-300 px-4"
                                                data-testid="checkout-coupon-apply"
                                            >
                                                {couponLoading ? 'Checking…' : 'Apply'}
                                            </Button>
                                        </div>
                                    </>
                                ) : (
                                    <div className="flex items-center justify-between bg-pink-50 rounded-lg px-3 py-2 border border-pink-100" data-testid="checkout-coupon-active">
                                        <div className="flex items-center gap-2">
                                            <span className="text-[10px] uppercase tracking-wider bg-pink-600 text-white px-2 py-0.5 rounded-full font-bold">{coupon.code}</span>
                                            <span className="text-xs font-body text-pink-700">applied — saving {formatPrice(couponDiscount)}</span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={removeCoupon}
                                            className="text-xs font-body text-pink-700 hover:text-pink-900 hover:underline underline-offset-2"
                                            data-testid="checkout-coupon-remove"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                )}
                            </div>
                            <Button onClick={handleSubmit} disabled={submitting} className="w-full mt-4 bg-pink-600 hover:bg-pink-700 rounded-full h-12 text-base font-body gap-2" data-testid="place-order-btn">
                                <CreditCard className="w-5 h-5" /> {submitting ? 'Processing...' : settings.paystack_public_key ? 'Pay with Paystack' : 'Place Order'}
                            </Button>
                            {!settings.paystack_public_key && <p className="text-xs text-gray-400 font-body text-center mt-2">Payment will be confirmed upon delivery</p>}
                            <div className="mt-3 pt-3 border-t">
                                <PaymentBadges className="justify-center" />
                            </div>
                            <div className="mt-3">
                                <TrustSignals compact />
                            </div>
                            <div className="mt-3">
                                <ReturnPolicy />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
